import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Flex, Form, notification, Spin } from "antd";
import { useAppDispatch, useAppSelector } from "../../../../../store/config";
import { IUploadFileParams } from "../../../../../interfaces/files/files";
import { ICreateRecipePayload } from "../../../../../interfaces/store/recipe-interface";
import { blobToBase64 } from "../../../../../utils/utils";
import {
  AbsoluteButton75,
  RelativeFlex,
  StyledColumnWrapper,
  StyledForm,
  StyledLongLayout,
  StyledColoredHeadline,
  StyledPlaceholder,
} from "../../../../../components/common/styles";
import {
  ImageInputField,
  StyledInputField,
  StyledInputLabel,
  TagsDropdownField,
  TextInputField,
} from "../../../../../components/inputs";
import { IMAGE_BASE_URL } from "../../../../../common/config";
import { useRecipeTagsOptions } from "../../../../trainers/useSelectOptions";
import {
  createRecipeStart,
  updateRecipeStart,
} from "../../../../../store/features/recipe/recipe-slice";
import { StyledAddStep } from "../../style";
import { ReactComponent as PlusIcon } from "../../../../../assets/icons/plus.svg";

export const RecipeDetails = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [form] = Form.useForm();
  const { recipeId } = useParams<{ recipeId: string }>();
  const [api, contextHolder] = notification.useNotification();
  const { isEdit } = location.state || {};

  const isNew: boolean = location.pathname.includes("/diet/add");

  const [isEditable, setIsEditable] = useState(false);
  const [imageData, setImageData] = useState<IUploadFileParams | null>(null);
  const [largeImageData, setLargeImageData] =
    useState<IUploadFileParams | null>(null);
  const [ingredients, setIngredients] = useState<string[]>([]);

  const { recipe, loading, error, requestState, operation } = useAppSelector(
    (state) => state.recipe
  );

  useEffect(() => {
    if (isNew || isEdit) {
      setIsEditable(true);
    }
  }, [isNew]);

  useEffect(() => {
    if (requestState === "finish") {
      if ((operation === "update" || operation === "create") && recipe) {
        setIsEditable(false);
        navigate(`/diet/${recipe._id}`, {
          replace: true,
        });
      }
    }
  }, [requestState, operation, recipe, navigate]);

  useEffect(() => {
    if (recipe) {
      form.setFieldsValue({
        name: recipe.name,
        ingredients: recipe.ingredients,
        protein: recipe.protein,
        carbs: recipe.carbs,
        fats: recipe.fats,
        calories: recipe.calories,
        tags: recipe.tags,
      });
    }
    setIngredients(
      recipe?.ingredients && recipe?.ingredients.length > 0
        ? recipe?.ingredients
        : isEditable
        ? [""]
        : []
    );
  }, [recipe, isEditable]);

  useEffect(() => {
    if (error) {
      openNotification();
    }
  }, [error]);

  const openNotification = (error?: string) => {
    api["error"]({
      message: t(error || "Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const upsertRecipe = async (values: any) => {
    if (!isEditable) {
      setIsEditable(true);
      return;
    }

    const filteredIngredients = ingredients.filter(
      (ingredient) => ingredient.trim() !== ""
    );

    const recipeBody = {
      name: values.name,
      ingredients: filteredIngredients,
      protein: values.protein,
      carbs: values.carbs,
      fats: values.fats,
      calories: values.calories,
      tags: values.tags || [],
      steps: recipe?.steps || [],
    };

    const upsertRecipeBody: ICreateRecipePayload = {
      recipe: { ...recipeBody },
    };

    if (imageData) {
      const base64Image = await blobToBase64(imageData.binaryFile as Blob);
      upsertRecipeBody.imageData = {
        ...imageData,
        binaryFile: base64Image,
        fileType: "image",
      };
    }

    if (largeImageData) {
      const base64Image = await blobToBase64(largeImageData.binaryFile as Blob);
      upsertRecipeBody.largeImageData = {
        ...largeImageData,
        binaryFile: base64Image,
        fileType: "image",
      };
    }

    if (isNew) {
      dispatch(createRecipeStart({ ...upsertRecipeBody }));
    } else if (recipeId) {
      dispatch(
        updateRecipeStart({
          ...upsertRecipeBody,
          recipeId: recipeId,
        })
      );
    }
  };

  const recipeTagsOptions = useRecipeTagsOptions();

  const addIngredient = () => {
    setIngredients((prevIngredients) => [...prevIngredients, ""]);
  };

  const updateIngredient = (index: number, value: string) => {
    setIngredients((prevIngredients) => {
      const newIngredients = [...prevIngredients];
      newIngredients[index] = value;
      return newIngredients;
    });
  };

  return (
    <>
      {contextHolder}
      <RelativeFlex>
        <AbsoluteButton75 onClick={() => form.submit()}>
          {isEditable ? t("Save") : t("Edit")}
        </AbsoluteButton75>
      </RelativeFlex>

      <Spin spinning={loading}>
        <StyledForm
          form={form}
          onFinish={upsertRecipe}
          validateTrigger="onSubmit"
        >
          <StyledLongLayout>
            <StyledColumnWrapper>
              <TextInputField
                name="name"
                label={t("Recipe Name")}
                isEditable={isEditable}
                rules={[{ required: true, message: t("Required") }]}
              />
              <Flex gap={20}>
                <TextInputField
                  name="calories"
                  label={t("Calories")}
                  isEditable={isEditable}
                  rules={[
                    { required: true, message: t("Required") },
                    {
                      pattern: /^[0-9]*$/,
                      message: t("Invalid number"),
                    },
                  ]}
                />
                <TextInputField
                  name="protein"
                  label={t("Protein")}
                  isEditable={isEditable}
                  rules={[
                    { required: true, message: t("Required") },
                    {
                      pattern: /^[0-9]*$/,
                      message: t("Invalid number"),
                    },
                  ]}
                />
                <TextInputField
                  name="carbs"
                  label={t("Carbs")}
                  isEditable={isEditable}
                  rules={[
                    { required: true, message: t("Required") },
                    {
                      pattern: /^[0-9]*$/,
                      message: t("Invalid number"),
                    },
                  ]}
                />
                <TextInputField
                  name="fats"
                  label={t("Fats")}
                  isEditable={isEditable}
                  rules={[
                    { required: true, message: t("Required") },
                    {
                      pattern: /^[0-9]*$/,
                      message: t("Invalid number"),
                    },
                  ]}
                />
              </Flex>

              <TagsDropdownField
                mode="multiple"
                label={t("Tags")}
                options={recipeTagsOptions}
                name="tags"
                isEditable={isEditable}
              />

              <Flex vertical gap={10}>
                <StyledInputLabel>{t("Ingredients")}</StyledInputLabel>
                {ingredients.length === 0 && !isEditable && (
                  <StyledPlaceholder>{t("Empty")}</StyledPlaceholder>
                )}
                <Flex gap={10} vertical>
                  {ingredients.map((ingredient, index) => {
                    return (
                      <StyledInputField
                        key={index}
                        isEditable={isEditable}
                        value={ingredient}
                        onChange={(e) =>
                          updateIngredient(index, e.target.value)
                        }
                      />
                    );
                  })}
                </Flex>
                {isEditable && (
                  <StyledAddStep
                    justify="start"
                    gap={5}
                    align="center"
                    onClick={addIngredient}
                  >
                    <PlusIcon height={20} width={20} />
                    <StyledColoredHeadline>{t("Add")}</StyledColoredHeadline>
                  </StyledAddStep>
                )}
              </Flex>
            </StyledColumnWrapper>
            <StyledColumnWrapper>
              <ImageInputField
                label={t("Recipe Outer Cover Image")}
                isEditable={isEditable}
                value={
                  recipe?.outerCover
                    ? IMAGE_BASE_URL + "/" + recipe.outerCover
                    : undefined
                }
                onChange={setImageData}
                aspect={170 / 95}
              />
              <ImageInputField
                label={t("Recipe Inner Cover Image")}
                isEditable={isEditable}
                value={
                  recipe?.innerCover
                    ? IMAGE_BASE_URL + "/" + recipe.innerCover
                    : undefined
                }
                onChange={setLargeImageData}
                aspect={390 / 214}
              />
            </StyledColumnWrapper>
          </StyledLongLayout>
        </StyledForm>
      </Spin>
    </>
  );
};
